import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { usePost } from '../../post';
import { usePostSettings } from '../../post/utils';
import { stableRound } from './stableRound';

const MAX_LINE_COUNT_CHANGES = 7;
const MAX_LINES = 10;

interface UseLinesCountProps {
  rawLinesCount: number;
  containerHeight: number;
  lineSize: number;
  lineDecrement: number;
}

export function useLinesCount({
  rawLinesCount,
  containerHeight,
  lineSize,
  lineDecrement
}: UseLinesCountProps) {
  const { heightNormalization } = usePostSettings();
  const { hasMedia, horizontal } = usePost();

  const computedLinesCount = useMemo(() => {
    if (heightNormalization || horizontal) {
      const roundedLinesCount =
        stableRound(containerHeight / lineSize) - lineDecrement;

      if (hasMedia) {
        const minMediaLinesCount = 3;

        return roundedLinesCount <= minMediaLinesCount
          ? rawLinesCount
          : roundedLinesCount;
      }

      if (roundedLinesCount > 0) {
        return roundedLinesCount;
      }
    }

    return rawLinesCount;
  }, [
    horizontal,
    hasMedia,
    rawLinesCount,
    lineSize,
    containerHeight,
    heightNormalization,
    lineDecrement
  ]);

  const [stableLinesCount, setStableLinesCount] = useState(computedLinesCount);
  const changeCountRef = useRef(0);

  useLayoutEffect(() => {
    changeCountRef.current = 0;
    setStableLinesCount(computedLinesCount);
  }, [rawLinesCount]);

  useLayoutEffect(() => {
    if (computedLinesCount === stableLinesCount) {
      changeCountRef.current = 0;

      return;
    }

    changeCountRef.current += 1;

    if (changeCountRef.current <= MAX_LINE_COUNT_CHANGES) {
      setStableLinesCount(Math.min(computedLinesCount, MAX_LINES));
    }
  }, [computedLinesCount]);

  return stableLinesCount;
}
